import { useReducer } from 'react';

import ReactHowler from 'react-howler';

import { appReducer, initialState } from './store/reducer';
import { renderBetButtonTitle, renderBetButtonText } from './utils/buttons';
import { CARD_IMAGES } from './utils/cards';
import { CHIPS } from './utils/chips';
import { BANKER, PLAYER, TIE, calcWinDifference } from './utils/utils';

import LayoutBetControls from './components/Layout/LayoutBetControls';
import LayoutContainer from './components/Layout/LayoutContainer';
import LayoutControls from './components/Layout/LayoutControls';
import LayoutMain from './components/Layout/LayoutMain';
import LayoutGameBackground from './components/Layout/LayoutGameBackground';

import Badge from './components/UI/Badge';
import Balance from './components/UI/Balance';
import BetAmount from './components/UI/BetAmount';
import Button from './components/UI/Button';
import ChipsPanel from './components/UI/ChipsPanel';
import Game from './components/UI/Game';
import GameCards from './components/UI/GameCards';

import IconBet from './components/Icons/IconBet';
import IconClear from './components/Icons/IconClear';
import IconMute from './components/Icons/IconMute';
import IconSound from './components/Icons/IconSound';
import IconWarning from './components/Icons/IconWarning';

import backgroundFx from './assets/audio/audio-backgroundFx.mp3';
import clearFx from './assets/audio/audio-clearFx.mp3';
import cardFx from './assets/audio/audio-cardFx.mp3';
import chipFx from './assets/audio/audio-chipFx.mp3';

function App() {
  const [state, dispatch] = useReducer(appReducer, initialState);

  const playerScoreCombined = state.playerCards.flatMap((score: any) => score.value);
  const bankerScoreCombined = state.bankerCards.flatMap((score: any) => score.value);
  const playerFinalScore = playerScoreCombined.reduce((a: number, b: number) => a + b, 0);
  const bankerFinalScore = bankerScoreCombined.reduce((a: number, b: number) => a + b, 0);
  const playerDifference = calcWinDifference(playerFinalScore);
  const bankerDifference = calcWinDifference(bankerFinalScore);
  const playerWins = playerDifference < bankerDifference;
  const bankerWins = playerDifference > bankerDifference;
  const tie = playerDifference === bankerDifference;

  const shuffledPlayerCards = [...CARD_IMAGES]
    .sort(() => Math.random() - 0.5)
    .splice(50)
    .map((card) => ({ ...card }));

  const shuffledBankerCards = [...CARD_IMAGES]
    .sort(() => Math.random() - 0.5)
    .splice(50)
    .map((card) => ({ ...card }));

  const renderChips = () => {
    return CHIPS.filter((chip) => chip.displayed).map((chip: any, idx: number) => (
      <Button
        key={idx}
        size={state.activeChip === chip.value ? 'unstyled is-active' : 'unstyled'}
        title={`Click to select ${chip.value} chip`}
        onClick={() => dispatch({ type: 'HANDLE_CHIP_CLICK', payload: chip.value })}
      >
        <img src={chip.src} alt={`${chip.value} chip`} />
      </Button>
    ));
  };

  const renderGameWinner = () => {
    if (playerWins) {
      return <Badge winner={PLAYER} text="Win" amount={state.totalWinAmount} />;
    } else if (bankerWins) {
      return <Badge winner={BANKER} text="Win" amount={state.totalWinAmount} />;
    } else if (state.gamesPlayed > 0 && tie) {
      return <Badge text="No win" />;
    }
  };

  const renderBetButtonIcon = () => (isBetButtonDisabled() ? <IconWarning /> : <IconBet />);

  const renderMusicButton = () => (state.backgroundFx ? <IconSound /> : <IconMute />);

  const handleMultiplier = (multiplier: number) => {
    if (state.betAmount < state.maxBet) {
      dispatch({ type: 'HANDLE_MULTIPLIER', payload: multiplier });
    }
  };

  const handleChosenOutcome = (outcome: string) => {
    if (state.betAmount <= state.maxBet) {
      dispatch({ type: 'HANDLE_CHOSEN_OUTCOME', payload: outcome });
    }
  };

  const isBetButtonDisabled = () =>
    state.chosenOutcome === '' ||
    state.betAmount < state.minBet ||
    state.betAmount > state.maxBet ||
    state.betAmount > state.totalBalance;

  const isClearButtonDisabled = () => state.betAmount >= state.minBet;

  return (
    <>
      <div className="overlay">
        Work in progress. The app is only available on screen sizes larger than 1200px.
      </div>
      <LayoutMain>
        <LayoutContainer>
          <LayoutGameBackground>
            <Balance balanceChange={state.balanceChange} totalBalance={state.totalBalance} />
            <Game
              renderPlayer={
                <GameCards
                  who={PLAYER}
                  winner={playerWins}
                  score={playerFinalScore}
                  cards={state.playerCards}
                />
              }
              renderBanker={
                <GameCards
                  who={BANKER}
                  winner={bankerWins}
                  score={bankerFinalScore}
                  cards={state.bankerCards}
                />
              }
              renderAmount={state.betAmount}
              renderOutcome={state.chosenOutcome}
              renderWinner={renderGameWinner()}
              handleTie={() => handleChosenOutcome(TIE)}
              handleBanker={() => handleChosenOutcome(BANKER)}
              handlePlayer={() => handleChosenOutcome(PLAYER)}
            />
            <LayoutControls>
              <Button
                title="Click to play background music"
                onClick={() => dispatch({ type: 'HANDLE_BACKGROUND_FX' })}
              >
                {renderMusicButton()}
              </Button>
              <Button
                title="Click to clear table"
                onClick={() => dispatch({ type: 'HANDLE_CLEAR_TABLE' })}
                disabled={!isClearButtonDisabled()}
              >
                <IconClear />
                Clear
              </Button>
            </LayoutControls>
            <LayoutBetControls>
              <BetAmount
                betAmount={state.betAmount}
                twoX={() => handleMultiplier(2)}
                threeX={() => handleMultiplier(3)}
              />
              <ChipsPanel minBet={state.minBet} maxBet={state.maxBet} renderChips={renderChips()} />
              <Button
                size="bet"
                title={renderBetButtonTitle(
                  state.betAmount,
                  state.minBet,
                  state.maxBet,
                  state.totalBalance
                )}
                onClick={() =>
                  dispatch({
                    type: 'HANDLE_BET_CLICK',
                    playerCards: shuffledPlayerCards,
                    bankerCards: shuffledBankerCards,
                  })
                }
                disabled={isBetButtonDisabled()}
              >
                {renderBetButtonIcon()}
                {renderBetButtonText(
                  state.betAmount,
                  state.minBet,
                  state.maxBet,
                  state.totalBalance
                )}
              </Button>
            </LayoutBetControls>
          </LayoutGameBackground>
        </LayoutContainer>
      </LayoutMain>
      <ReactHowler src={backgroundFx} playing={state.backgroundFx} loop={true} preload={false} />
      <ReactHowler src={chipFx} playing={state.chipFx} preload={false} />
      <ReactHowler src={cardFx} playing={state.cardFx} preload={false} />
      <ReactHowler src={clearFx} playing={state.clearFx} preload={false} />
    </>
  );
}

export default App;
